import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { ktx2Extend } from '../utils/ktx2';

// CarModel — one real car, auto-fitted so importing a new one is a file drop.
//
// EVERY IMPORTED CAR ARRIVES WRONG in a different way: a different unit scale, a
// different origin (wheels on the floor, or the body centred in mid-air), and a
// different idea of which axis is "forwards". Asking whoever adds a car to work
// those three numbers out by hand is how a car library stops growing.
//
// So all three are measured here:
//   SCALE   longest horizontal axis fitted to `length` (the car's real length in
//           metres, so a pickup and a Lotus keep their true proportions)
//   GROUND  the model's own lowest point dropped to y=0, so it rests on the road
//           instead of floating or sinking
//   FACING  if the model is wider than it is deep, it was authored side-on —
//           turn it a quarter so its length runs along travel
//
// cars.json can override any of them per car, but nothing has to. The one thing
// measurement CANNOT recover is which end is the nose — no property of the
// geometry says so — which is what `rotY` in the manifest is for.
export default function CarModel({
  url, length = 4, scale: scaleOverride = null, rotY: rotYOverride = null, onReady = null,
  // Repaint the BODY (Spor1's yellow Lotus): opaque materials take the tint,
  // glass keeps its transparency. Tinted materials are CLONED first — clone()
  // shares materials with the GLTF cache, so painting in place would turn
  // every Elise in traffic yellow too.
  tint = null,
}) {
  // KTX2 textures stay GPU-compressed instead of decoding to full RGBA, which is
  // most of why a car is cheap to bring in. Inert on GLBs without them, so it is
  // safe on every car whether or not that one was encoded.
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const { scene } = useGLTF(url, true, true, ktx2);

  const { model, fit } = useMemo(() => {
    const m = scene.clone(true);
    if (tint) {
      // REPAINTING A TEXTURED CAR. Setting material.color only MULTIPLIES the
      // baked texture — the Elise's paint lives in its atlas under a white
      // base color, so a multiply changed nothing visible. The working tool is
      // a luminance-preserving colorize injected into the shader: the texture
      // keeps every bit of its shading and detail, its COLOR becomes the tint.
      // The injected code is identical for every tint (uniform-driven), so all
      // painted variants share ONE compiled program — five colours, one
      // compile. Tires/wheels are skipped by name; true glass by flag.
      const tintCol = new THREE.Color(tint);
      const painted = new Map();   // shared material → its painted clone
      m.traverse((n) => {
        if (!n.isMesh || !n.material) return;
        const paint = (mat) => {
          if (mat.transparent) return mat;                       // glass stays glass
          if (/tire|wheel|rubber/i.test(mat.name || '')) return mat;
          if (!painted.has(mat)) {
            const c = mat.clone();
            c.onBeforeCompile = (shader) => {
              shader.uniforms.uCarTint = { value: tintCol };
              shader.fragmentShader = ('uniform vec3 uCarTint;\n' + shader.fragmentShader).replace(
                '#include <map_fragment>',
                `#include <map_fragment>
                {
                  float carLum = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));
                  diffuseColor.rgb = mix(diffuseColor.rgb, clamp(carLum * 2.0, 0.0, 1.2) * uCarTint, 0.85);
                }`
              );
            };
            painted.set(mat, c);
          }
          return painted.get(mat);
        };
        n.material = Array.isArray(n.material) ? n.material.map(paint) : paint(n.material);
      });
    }
    const box = new THREE.Box3().setFromObject(m);
    const size = new THREE.Vector3(); box.getSize(size);
    const centre = new THREE.Vector3(); box.getCenter(centre);

    // Wider than deep → authored across the direction of travel.
    const sideOn = size.x > size.z;
    const longest = Math.max(size.x, size.z) || 1;
    const s = scaleOverride ?? length / longest;
    const rotY = rotYOverride ?? (sideOn ? Math.PI / 2 : 0);

    return {
      model: m,
      fit: {
        s,
        rotY,
        // Centre it on its own footprint and sit it on the ground, in that order.
        offset: [-centre.x * s, -box.min.y * s, -centre.z * s],
      },
    };
  }, [scene, length, scaleOverride, rotYOverride, tint]);

  // COMPILE THE SHADERS NOW, NOT WHEN IT DRIVES INTO FRAME.
  //
  // A material becomes a GPU program the first time it is actually rendered, and
  // these cars are frustum-culled until they round a corner — so the compile
  // happens mid-drive, on the main thread, in whatever frame the car first
  // becomes visible. One material would be unnoticeable. The Traverse has 31 and
  // the Monza 53 (the Lotus, for contrast, has 2), so what arrives is a stall
  // exactly as a car comes into view, which is precisely how it was described.
  //
  // compileAsync warms them off the critical path while the staged reveal is
  // still bringing cars in one at a time — a moment that is already budgeted for
  // loading. The promise is deliberately not awaited before onReady: the car is
  // parsed and safe to show either way, and blocking the reveal chain on shader
  // compilation would just move the stall rather than hide it.
  const camera = useThree((s) => s.camera);
  useEffect(() => {
    if (!model || !gl.compileAsync) return;
    let dead = false;
    gl.compileAsync(model, camera).catch(() => { /* it compiles on first draw */ });
    return () => { dead = true; void dead; };
  }, [model, gl, camera]);

  // The honest "I am loaded" signal for the fleet's one-at-a-time reveal: by the
  // time this runs, Suspense has resolved and the GLTF is parsed.
  useEffect(() => { onReady?.(); }, [model, onReady]);

  // NOTHING IS DISPOSED HERE ON PURPOSE. Object3D.clone() copies the node but
  // SHARES geometry and material with the cached scene, so disposing a clone's
  // geometry destroys the buffers every other copy of that car — and the
  // useGLTF cache entry itself — is still drawing from. The model stays in the
  // cache for the session by design; it is a handful of cars, not a leak.

  return (
    <group rotation={[0, fit.rotY, 0]}>
      <group position={fit.offset} scale={fit.s}>
        <primitive object={model} />
      </group>
    </group>
  );
}
